import React, { useState, useEffect, useCallback } from 'react';
import { getCognitiveTracker } from '../../../services/cognitiveSkills';
import AdaptiveModule from '../AdaptiveModule';
import './style.css';

// Unified Assessment Wrapper
// Handles both adaptive and non-adaptive modules with scoring
const AssessmentWrapper = ({ 
  children,
  moduleId,
  moduleType, // '1A', '1B', '2', '3', '4A', '4B', '4C', '5', '6', '7', '8'
  onModuleComplete,
  isAdaptive = false,
  renderAdaptiveContent = null
}) => {
  const [tracker] = useState(() => getCognitiveTracker());
  const [moduleState, setModuleState] = useState(null);
  const [scoreNotifications, setScoreNotifications] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize module tracking
  useEffect(() => {
    if (!isInitialized && moduleType) {
      const state = tracker.startModule(moduleType);
      setModuleState(state);
      setIsInitialized(true);
      
      console.log(`Module ${moduleType} initialized:`, state);
    }
  }, [moduleType, tracker, isInitialized]);

  // Handle scoring for non-adaptive modules
  const recordScore = useCallback((isCorrect, metadata = {}) => {
    if (!moduleType) return;
    
    // Use custom points if provided (for Module 8 per-digit scoring)
    const customPoints = metadata.customPoints;
    const result = tracker.recordAnswer(moduleType, isCorrect, null, null, customPoints);
    
    // Show score feedback
    const pointsToShow = customPoints !== undefined ? customPoints : (result?.points || 2);
    
    // Module 8: Show feedback for any score (including 0)
    // Other modules: Only show feedback for correct answers
    const shouldShowFeedback = moduleType === '8' ? true : (isCorrect && pointsToShow > 0);
    
    console.log(`🎯 Score Display Debug - Module ${moduleType}:`, {
      shouldShowFeedback,
      pointsToShow,
      isCorrect,
      customPoints,
      isModule8: moduleType === '8'
    });
    
    if (shouldShowFeedback) {
      console.log('✅ Adding score notification');
      const notificationId = Date.now() + Math.random();
      const newNotification = {
        id: notificationId,
        points: pointsToShow,
        isCorrect: moduleType === '8' ? (pointsToShow > 0) : isCorrect
      };
      
      setScoreNotifications(prev => [...prev, newNotification]);
      
      // Remove this notification after 2 seconds
      setTimeout(() => {
        setScoreNotifications(prev => prev.filter(n => n.id !== notificationId));
      }, 2000);
    }
    
    // Log for debugging
    console.log(`Module ${moduleType} - Answer recorded:`, {
      isCorrect,
      result,
      metadata,
      pointsAwarded: pointsToShow
    });
    
    return result;
  }, [moduleType, tracker]);

  // Handle module completion
  const handleModuleComplete = useCallback((completionData = {}, delay = 0) => {
    // If there's a delay (for showing score), wait before completing
    setTimeout(() => {
      const finalState = tracker.getState();
      const percentiles = tracker.calculatePercentiles();
      
      console.log(`Module ${moduleType} completed:`, {
        percentiles,
        finalState,
        completionData
      });
      
      if (onModuleComplete) {
        onModuleComplete({
          moduleType,
          percentiles,
          finalState,
          ...completionData
        });
      }
    }, delay);
  }, [moduleType, tracker, onModuleComplete]);

  // For adaptive modules, use the AdaptiveModule component
  if (isAdaptive && renderAdaptiveContent) {
    return (
      <div className="assessment-wrapper adaptive">
        <AdaptiveModule
          moduleId={moduleType}
          onComplete={handleModuleComplete}
          renderContent={renderAdaptiveContent}
        />
      </div>
    );
  }

  // For non-adaptive modules, wrap children with scoring context
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        recordScore,
        handleModuleComplete,
        moduleState,
        tracker
      });
    }
    return child;
  });

  // Determine difficulty for display
  const difficulty = moduleType?.startsWith('4') ? 1 : 2;

  console.log('🔍 AssessmentWrapper render - scoreNotifications:', scoreNotifications);
  
  return (
    <div className="assessment-wrapper non-adaptive">
      {scoreNotifications.map((notification, index) => (
        <div 
          key={notification.id}
          className={`score-popup ${notification.isCorrect ? 'correct' : 'incorrect'}`}
          style={{ 
            left: `calc(50% + ${(index - (scoreNotifications.length - 1) / 2) * 200}px)`,
            transform: 'translateX(-50%)'
          }}
        >
          <span className="score-text">+{notification.points} point{notification.points !== 1 ? 's' : ''}!</span>
        </div>
      ))}
      
      <div className="module-info-bar">
        <span className="module-label">Module {moduleType}</span>
        <span className="difficulty-label">Difficulty: {difficulty}/10 (Fixed)</span>
      </div>
      
      {childrenWithProps}
    </div>
  );
};

// HOC for easy module wrapping
export const withAssessment = (Component, moduleConfig) => {
  return (props) => (
    <AssessmentWrapper {...moduleConfig}>
      <Component {...props} />
    </AssessmentWrapper>
  );
};

// Module configuration helper
export const getModuleConfig = (moduleType) => {
  const adaptiveModules = ['2', '3', '5', '6', '7'];
  
  return {
    moduleType,
    isAdaptive: adaptiveModules.includes(moduleType),
    moduleId: `module-${moduleType}`
  };
};

export default AssessmentWrapper;